import { useGSAP } from '@gsap/react';
import { animateWithGsap } from '../utils/animations';
import { ADDONS, PLATFORMS, PRICING, OFFER, PAGE_OPTIONS } from '../constants';
import { fmt, offerActive, wpDelta } from '../utils';
import Receipt from './Receipt';
import { useCountdown, clockText } from '../hooks/useCountdown';

import InfoTip from './InfoTip';

const Question = ({ id, n, title, children }) => (
  <section id={id} className="py-12 md:py-16">
    <p className="eyebrow g_fadeIn">{n} of 3</p>
    <h2 className="step-title g_fadeIn">{title}</h2>
    <div className="mt-8">{children}</div>
  </section>
);

const Option = ({ on, onClick, title, desc, priceLabel, marketLabel, tag, note, disabled, info }) => (
  <button type="button" onClick={onClick} disabled={disabled} className={`opt overflow-visible g_fadeIn !gap-1 ${on ? 'on' : ''} ${disabled ? '!border-dashed !bg-zinc text-gray cursor-not-allowed' : ''}`}>
    {tag && <span className="opt-tag">{tag}</span>}
    <div className="flex items-start justify-between gap-3">
      <span className="text-lg font-semibold">{title}</span>
      <div className="flex items-center gap-2">
        {info && <InfoTip text={info} />}
        {!disabled && <span className="opt-check">✓</span>}
      </div>
    </div>
    <span className="text-sm text-gray">{desc}</span>
    <span className="mt-2 flex items-baseline gap-2">
      {marketLabel && <span className="tabular text-xs text-gray line-through">{marketLabel}</span>}
      <span className={`tabular text-sm font-semibold ${priceLabel === 'Included' || priceLabel === 'Included with WordPress' ? 'text-gray' : priceLabel === 'Free' ? 'text-[#1d9c5e]' : ''}`}>{priceLabel}</span>
    </span>
    {note && <span className="mt-1 text-xs font-medium text-[#1d9c5e]">{note}</span>}
  </button>
);

const Configurator = ({ build }) => {
  useGSAP(() => {
    animateWithGsap('.g_fadeIn', { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' });
  }, []);

  const base = PRICING.platforms.static.price;
  const countdown = useCountdown();

  return (
    <div id="build" className="common-padding !py-0 bg-white">
      <div className="screen-max-width grid grid-cols-1 gap-16 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="lg:pb-16">
          <Question id="platform" n="1" title="Which platform?">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {PLATFORMS.map((p) => {
                let priceLabel = p.delta === 0 ? `₹${fmt(base)}` : `+ ₹${fmt(p.delta)}`;
                let marketLabel = p.delta === 0 ? `₹${fmt(p.market)}` : `+ ₹${fmt(p.market)}`;
                let tag;
                let note;

                if (p.id === 'wp') {
                  if (countdown.active) {
                    priceLabel = 'Free';
                    marketLabel = `+ ₹${fmt(p.delta)}`;
                    tag = 'Limited offer';
                    note = `Your offer ends in ${clockText(countdown)}`;
                  } else {
                    priceLabel = `+ ₹${fmt(wpDelta())}`;
                  }
                }

                return (
                  <Option
                    key={p.id}
                    on={build.platform === p.id}
                    onClick={() => build.setPlatform(p.id)}
                    title={p.title}
                    desc={p.desc}
                    info={p.info}
                    priceLabel={priceLabel}
                    marketLabel={marketLabel}
                    tag={tag}
                    note={note}
                  />
                );
              })}
            </div>
          </Question>

          <Question id="pages" n="2" title="Which pages do you need?">
            <div className="g_fadeIn flex items-baseline justify-between">
              <span className="text-sm text-ink">{build.totalPages} pages selected <span className="text-gray">{build.extraPages === 0 ? ` · ${PRICING.basePages} included, extra pages ₹${fmt(PRICING.perPage)} each` : ` · ${PRICING.basePages} included + ${build.extraPages} extra`}</span></span>
              <span className="flex items-baseline gap-2">
                {build.extraPages > 0 && <span className="tabular text-xs text-gray line-through">+ ₹{fmt(build.extraPages * PRICING.perPageMarket)}</span>}
                <span className="tabular text-sm font-semibold">{build.extraPages === 0 ? 'Included' : `+ ₹${fmt(build.extraPages * PRICING.perPage)}`}</span>
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PAGE_OPTIONS.map((name) => {
                const isHome = name === 'Home';
                const checked = build.pages.includes(name);
                const disabled = !checked && build.pages.length + (build.otherOn ? 1 : 0) >= PRICING.basePages + PRICING.maxExtraPages;
                return (
                  <label key={name} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm cursor-pointer transition ${checked ? 'border-blue bg-[#E8F0FF] text-ink' : 'border-[#E2E8F0] bg-white text-ink'} ${disabled && !isHome ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <input type="checkbox" className="h-4 w-4 accent-[#0F5EFF]" checked={checked} disabled={disabled || isHome} onChange={() => build.togglePage(name)} />
                    <span>{name} {isHome && <span className="text-gray text-xs">(always)</span>}</span>
                  </label>
                );
              })}
              <label className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm cursor-pointer transition ${build.otherOn ? 'border-blue bg-[#E8F0FF] text-ink' : 'border-[#E2E8F0] bg-white text-ink'} ${!build.otherOn && build.pages.length >= PRICING.basePages + PRICING.maxExtraPages ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <input type="checkbox" className="h-4 w-4 accent-[#0F5EFF]" checked={build.otherOn} disabled={!build.otherOn && build.pages.length >= PRICING.basePages + PRICING.maxExtraPages} onChange={(e) => build.setOtherOn(e.target.checked)} />
                <span>Other page</span>
              </label>
              
              {build.otherOn && (
                <div className="col-span-full">
                  <input type="text" placeholder="Page name, e.g. Menu" maxLength={40} className="rounded-2xl border border-[#E2E8F0] px-4 py-3 text-sm w-full focus:border-blue outline-none" value={build.otherName} onChange={(e) => build.setOtherName(e.target.value)} autoFocus />
                </div>
              )}
            </div>
          </Question>

          <Question id="extras" n="3" title="Anything else?">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {ADDONS.map((a) => {
                const disabled = build.platform === 'wp' && a.notWithWp;
                return (
                  <Option
                    key={a.id}
                    on={build.addons.includes(a.id)}
                    onClick={disabled ? () => {} : () => build.toggleAddon(a.id)}
                    title={a.title}
                    desc={a.desc}
                    info={a.info}
                    priceLabel={disabled ? 'Included with WordPress' : `+ ₹${fmt(a.price)}`}
                    marketLabel={disabled ? undefined : `+ ₹${fmt(a.market)}`}
                    tag={a.tag}
                    disabled={disabled}
                  />
                );
              })}
            </div>
          </Question>
        </div>

        <aside className="hidden lg:block">
          <Receipt build={build} className="sticky top-24" />
        </aside>
      </div>
    </div>
  );
};

export default Configurator;
